import React, { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "../../contexts/authContext/index.jsx";
import MainTabs from "../partials/main-tabs.jsx";
import { useParams } from "react-router-dom";
import { editRecord, deleteRecord, viewRecord } from "../../firebase/db/catalog/crud.tsx";
import { Link, useNavigate } from "react-router-dom";
import { Timestamp } from "firebase/firestore";
import { useUploadAudio, deleteAudio } from "../../firebase/db/files/crud.tsx";
import Dropzone from "react-dropzone";
import WavesurferPlayer from "@wavesurfer/react";
import WaveSurfer from "wavesurfer.js";
import ShareConfigurationModal from "./share-configuration.tsx";

interface CatalogData {
	id?: string;
	audio_url?: string | URL;
	audio_file_name?: string;
	bpm?: number;
	created?: Timestamp;
	demo_exclusive_to?: string;
	internal_song_name?: string;
	is_demo_exclusive?: boolean;
	is_released?: boolean;
	key?: string;
	length?: string;
	modified?: Timestamp;
	description?: string;
	song_name?: string;
	song_status?: string;
	user_id?: string; // Assuming you have a 'user' field
}

const HomeView = () => {
	const { currentUser } = useAuth();
	const navigate = useNavigate();
	const { id } = useParams();
	const [data, setData] = useState<CatalogData | null>(null);
	const [createdDate, setCreatedDate] = useState(String);
	const [modifiedDate, setModifiedDate] = useState(String);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [file, setFile] = useState<Blob | null>(null);
	const [fileName, setFileName] = useState(String);
	const { progress, uploadError, downloadURL, loading, upload } = useUploadAudio();
	const [showPlayer, setShowPlayer] = useState<Boolean>(false);
	const [replacingFile, setReplacingFile] = useState<Boolean>(false);
	const [isWaveLoading, setIsWaveLoading] = useState<Boolean>(true);
	const [isModalOpen, setModalOpen] = useState<Boolean>(false);

	function handleDeleteRecord(recordId) {
		if (data?.audio_url && data?.audio_file_name) {
			handleFileDelete(recordId, data.audio_file_name);
		}
		deleteRecord(recordId);
		navigate(`/home`);
	}

	function handleReplaceFile() {
		setShowPlayer(false);
		setReplacingFile(true);
	}

	function cancelReplaceFile() {
		setReplacingFile(false);
		setIsWaveLoading(true);
		setShowPlayer(true);
	}

	function handleReleaseSong(recordId) {}

	function handleStatusChange(newStatus: string) {
		const status = {
			song_status: newStatus
		};
		editRecord(id, status);
		data.song_status = newStatus;
	}

	const openDownloadTab = () => {
		console.log("Download URL: " + data?.audio_url);
		window.open(data?.audio_url, "_blank");
	};

	function downloadAudioFile() {
		if (data && data.audio_url && data.audio_file_name) {
			const xhr = new XMLHttpRequest();
			xhr.responseType = "blob";
			xhr.onload = (event) => {
				const blob = new Blob([xhr.response], { type: "audio/wav" });
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = data.audio_file_name;
				link.click();
				URL.revokeObjectURL(url);
			};
			xhr.open("GET", data.audio_url);
			xhr.send();
		}
	}

	useEffect(() => {
		const fetchDataAndSetState = async () => {
			try {
				console.log("Fetching data...");
				const fetchedData = await viewRecord(id);
				if (fetchedData !== null) {
					setCreatedDate(getFormattedDate(fetchedData?.created));
					setModifiedDate(getFormattedDate(fetchedData?.modified));
					setData(fetchedData);
					if (fetchedData.audio_url) {
						setShowPlayer(true);
					}
				} else {
					setData({ audio_url: "" });
				}
			} catch (e) {
				setError(e); //More informative error message
			} finally {
				setIsLoading(false);
			}
		};

		fetchDataAndSetState();
	}, [id]);

	const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);

	const onReady = (ws) => {
		setIsWaveLoading(false);
		setWavesurfer(ws);
		setIsPlaying(false);
		setIsWaveLoading(false);
	};

	const onPlayPause = () => {
		wavesurfer?.playPause();
	};

	const onDrop = (acceptedFiles: any[]) => {
		console.log("made it to on drop. replacing file? " + replacingFile);
		if (acceptedFiles.length > 0 && !loading) {
			//Check for isUploading
			console.log("made it to accepted files");
			const acceptedFile = acceptedFiles[0];
			setFile(acceptedFile);
			setFileName(acceptedFile.name);
			upload(acceptedFile, acceptedFile.name, currentUser.uid, handleUploadComplete);
		}
	};

	const handleUploadComplete = useCallback(
		async (url, fileName) => {
			if (id) {
				try {
					if (replacingFile) {
						handleFileDelete(id, data?.audio_file_name);
						setReplacingFile(false);
					} else {
						console.log("File Name: " + fileName);
						const audioPointer = {
							audio_url: url,
							audio_file_name: fileName
						};
						await editRecord(id, audioPointer); // Await here
						if (data) {
							data.audio_file_name = fileName;
							data.audio_url = url;
						}
					}
				} catch (e) {
					console.log("Error: " + e);
				} finally {
					setShowPlayer(true);
				}
			}
		},
		[id, replacingFile, data?.audio_file_name]
	);

	async function handleFileDelete(id, audioFileName) {
		deleteAudio(currentUser.uid, audioFileName);
		const audioPointer = {
			audio_url: "",
			audio_file_name: ""
		};
		await editRecord(id, audioPointer); // Await here
		data.audio_file_name = "";
		data.audio_url = "";
		setShowPlayer(false);
	}

	const acceptedTypes = {
		"audio/wav": [".wav"],
		"audio/mp3": [".mp3"]
	};

	function getFormattedDate(date) {
		if (date) {
			return (
				date.toDate().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) + " " + date.toDate().toLocaleDateString("en-US")
			);
		} else {
			return "";
		}
	}

	if (isLoading) {
		return (
			<div className="text-center">
				<div role="status">
					<svg
						aria-hidden="true"
						className="inline w-10 h-10 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
						viewBox="0 0 100 101"
						fill="none"
						xmlns="http://www.w3.org/2000/svg">
						<path
							d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
							fill="currentColor"
						/>
						<path
							d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
							fill="currentFill"
						/>
					</svg>
					<span className="sr-only">Loading...</span>
				</div>
			</div>
		);
	}

	if (error) {
		return <div>Error: {error}</div>;
	}

	if (data) {
		return (
			<div className="main-tabs">
				<div className="mx-auto">
					<MainTabs tabIndexSelected={0} />
					<div id="myTabContent">
						<div className="flex justify-between w-full page-header">
							<div className="page-heading">
								<img src="../../../assets/icons/music-room-icon.png" alt="Music Room" className="icon-large" />
								<div className="text-xs font-bold dark:text-white">
									<span className="mb-0 text-transparent bg-clip-text bg-gradient-to-r to-yellow-500 from-amber-700">SONG</span>
									<br />
									<div className="mt-0 text-3xl font-medium dark:text-white">
										{data.song_name ? data.song_name + " (" + data.internal_song_name + ")" : data.internal_song_name}
									</div>
								</div>
							</div>
							<div>
								<ol className="flex items-center w-full py-[10px] px-[16px] space-x-2 text-sm font-medium text-center text-gray-500 bg-white border border-gray-200 rounded-lg shadow-xs dark:text-gray-400 sm:text-base dark:bg-gray-800 dark:border-gray-700 sm:space-x-4 rtl:space-x-reverse">
									<li className={`flex items-center ${data.song_status === "New" ? "text-yellow-500" : ""}`}>
										<span
											className={`${
												data.song_status === "New" ? "border-yellow-500" : ""
											} flex items-center justify-center w-5 h-5 me-2 text-xs border rounded-full shrink-0`}>
											1
										</span>
										<button
											disabled={data.song_status === "New" ? true : false}
											className={`font-medium ${data.song_status === "New" ? "text-yellow-500" : ""}`}
											onClick={() => handleStatusChange("New")}>
											New
										</button>
										<svg
											className="w-3 h-3 ms-2 sm:ms-4 rtl:rotate-180"
											aria-hidden="true"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 12 10">
											<path
												stroke="currentColor"
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="m7 9 4-4-4-4M1 9l4-4-4-4"
											/>
										</svg>
									</li>
									<li className={`flex items-center ${data.song_status === "In-Progress" ? "text-yellow-500" : ""}`}>
										<span
											className={`${
												data.song_status === "In-Progress" ? "border-yellow-500" : ""
											} flex items-center justify-center w-5 h-5 me-2 text-xs border rounded-full shrink-0`}>
											2
										</span>
										<button
											disabled={data.song_status === "In-Progress" ? true : false}
											className={`font-medium ${data.song_status === "In-Progress" ? "text-yellow-500" : ""}`}
											onClick={() => handleStatusChange("In-Progress")}>
											In-Progress
										</button>
										<svg
											className="w-3 h-3 ms-2 sm:ms-4 rtl:rotate-180"
											aria-hidden="true"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 12 10">
											<path
												stroke="currentColor"
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="m7 9 4-4-4-4M1 9l4-4-4-4"
											/>
										</svg>
									</li>
									<li className={`flex items-center ${data.song_status === "Completed" ? "text-yellow-500" : ""}`}>
										<span
											className={`${
												data.song_status === "Completed" ? "border-yellow-500" : ""
											} flex items-center justify-center w-5 h-5 me-2 text-xs border rounded-full shrink-0`}>
											3
										</span>
										<button
											disabled={data.song_status === "Completed" ? true : false}
											className={`font-medium ${data.song_status === "Completed" ? "text-yellow-500" : ""}`}
											onClick={() => handleStatusChange("Completed")}>
											Completed
										</button>
										<svg
											className="w-3 h-3 ms-2 sm:ms-4 rtl:rotate-180"
											aria-hidden="true"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 12 10">
											<path
												stroke="currentColor"
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="m7 9 4-4-4-4M1 9l4-4-4-4"
											/>
										</svg>
									</li>
									<li className={`flex items-center ${data.song_status === "Shopping" ? "text-yellow-500" : ""}`}>
										<span
											className={`${
												data.song_status === "Shopping" ? "border-yellow-500" : ""
											} flex items-center justify-center w-5 h-5 me-2 text-xs border rounded-full shrink-0`}>
											4
										</span>
										<button
											disabled={data.song_status === "Shopping" ? true : false}
											className={`font-medium ${data.song_status === "Shopping" ? "text-yellow-500" : ""}`}
											onClick={() => handleStatusChange("Shopping")}>
											Shopping
										</button>
									</li>
								</ol>
							</div>
							<div>
								<div className="inline-flex rounded-md shadow-xs" role="group">
									<div className="inline-flex rounded-md shadow-xs" role="group">
										<button
											onClick={() => handleDeleteRecord(id)}
											type="button"
											className="rounded-s-lg border border-r-0 text-slate-400 hover:text-cyan-700 border-slate-600 focus:outline-none focus:ring-2 focus:text-cyan-700 focus:ring-cyan-700 font-medium text-sm px-4 py-2 mb-2">
											Delete
										</button>
										<button
											onClick={() => handleReleaseSong(id)}
											type="button"
											className="rounded-e-lg mr-4 border text-slate-400 hover:text-cyan-700 border-slate-600 focus:outline-none focus:ring-2 focus:text-cyan-700 focus:ring-cyan-700 font-medium text-sm px-4 py-2 me-2 mb-2">
											Release
										</button>
									</div>
								</div>
							</div>
						</div>
						<div className="grid grid-cols-4 gap-5">
							<div
								className="height-auto col-span-3 shadow-xl bg-slate-500 py-4 px-5 rounded-lg"
								id="profile"
								role="tabpanel"
								aria-labelledby="profile-tab">
								<div className="mb-2 flex items-center justify-between flex-column flex-wrap md:flex-row space-y-4 md:space-y-0 pb-4">
									<div>
										<h2 className="text-3xl font-medium">Detail</h2>
									</div>
									<div className="relative">
										<div className="inline-flex rounded-md shadow-xs" role="group">
											<button
												onClick={() => navigate(`/home/edit/${id}`)}
												type="button"
												className="px-4 py-2 text-sm font-light text-gray-900 bg-white border border-r-0 border-gray-200 rounded-s-lg hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:text-white dark:hover:bg-gray-700 dark:focus:ring-blue-500 dark:focus:text-white">
												Edit
											</button>
											<button
												onClick={() => navigate(`/home`)}
												type="button"
												className="px-4 py-2 text-sm font-light text-gray-900 bg-white border border-gray-200 rounded-e-lg hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:text-white dark:hover:bg-gray-700 dark:focus:ring-blue-500 dark:focus:text-white">
												Close
											</button>
										</div>
									</div>
								</div>
								<form className="">
									<div className="flex gap-8">
										<div className="flex-auto">
											<div className="grid md:grid-cols-2 md:gap-6">
												<div className="relative z-0 w-full mb-5 group">
													<input
														type="text"
														name="internal_song_name"
														id="internal_song_name"
														value={data.internal_song_name}
														className="block py-2.5 px-0 w-full text-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
														placeholder=" "
														disabled
													/>
													<label
														for="internal_song_name"
														className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-800 font-medium duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">
														Song Name
													</label>
												</div>
												<div className="relative z-0 w-full mb-5 group">
													<input
														type="text"
														name="song_name"
														id="song_name"
														className="block py-2.5 px-0 w-full text-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
														placeholder=" "
														value={data.song_name}
														disabled
													/>
													<label
														for="song_name"
														className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-800 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">
														Published Song Name
													</label>
												</div>
											</div>
											<div className="grid md:grid-cols-2 md:gap-6">
												<div className="relative z-0 w-full mb-5 group">
													<input
														type="text"
														name="bpm"
														id="bpm"
														className="block py-2.5 px-0 w-full text-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
														placeholder=" "
														value={data.bpm}
														disabled
													/>
													<label
														for="bpm"
														className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-800 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">
														BPM
													</label>
												</div>
												<div className="relative z-0 w-full mb-5 group">
													<input
														type="text"
														name="key"
														id="key"
														className="block py-2.5 px-0 w-full text-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
														placeholder=" "
														value={data.key}
														disabled
													/>
													{/* <PhoneInput
												name="mobile"
												id="mobile"
												className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
												placeholder="Enter phone number"
												value={value}
												onChange={setValue}
												defaultCountry="US"
											/> */}
													<label
														for="key"
														className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-800 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">
														Key
													</label>
												</div>
											</div>
											<div className="grid md:grid-cols-1 md:gap-6">
												<div className="relative z-0 w-full mb-5 group">
													<input
														type="text"
														name="description"
														id="description"
														className="block py-2.5 px-0 w-full text-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
														placeholder=" "
														value={data.description}
														disabled
													/>
													<label
														for="description"
														className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-800 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">
														Description
													</label>
												</div>
											</div>
											<div className="grid md:grid-cols-2 md:gap-6">
												<div className="relative z-0 w-full mb-5 group">
													<input
														type="text"
														name="created"
														id="created"
														className="block py-2.5 px-0 w-full text-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
														placeholder=" "
														value={createdDate}
														disabled
													/>
													<label
														for="created"
														className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-800 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">
														Created
													</label>
												</div>
												<div className="relative z-0 w-full mb-5 group">
													<input
														type="text"
														name="modified"
														id="modified"
														className="block py-2.5 px-0 w-full text-medium text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
														placeholder=" "
														value={modifiedDate}
														disabled
													/>
													<label
														for="modified"
														className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-800 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">
														Modified
													</label>
												</div>
											</div>
										</div>
									</div>
								</form>
							</div>
							<div
								className="height-auto shadow-xl bg-slate-500 py-4 px-5 pb-6 rounded-lg"
								id="profile"
								role="tabpanel"
								aria-labelledby="profile-tab">
								<div className="mb-2 flex items-center justify-between flex-column flex-wrap md:flex-row space-y-4 md:space-y-0 pb-4">
									<div>
										<h2 className="text-3xl font-medium">Audio File</h2>
									</div>
									{showPlayer && (
										<div className="relative">
											<div className="inline-flex rounded-md shadow-xs" role="group">
												<button
													onClick={handleReplaceFile}
													type="button"
													className="px-4 py-2 text-sm font-light text-gray-900 bg-white border border-gray-200 rounded-s-lg hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:text-white dark:hover:bg-gray-700 dark:focus:ring-blue-500 dark:focus:text-white">
													<svg
														className="w-[15px] h-[15px] fill-[#ffffff]"
														viewBox="0 0 512 512"
														xmlns="http://www.w3.org/2000/svg">
														<path d="M142.9 142.9c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2s12.5 14.8 22.2 14.8H463.5c0 0 0 0 0 0H472c13.3 0 24-10.7 24-24V72c0-9.7-5.8-18.5-14.8-22.2s-19.3-1.7-26.2 5.2L413.4 96.6c-87.6-86.5-228.7-86.2-315.8 1C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5c7.7-21.8 20.2-42.3 37.8-59.8zM16 312v7.6 .7V440c0 9.7 5.8 18.5 14.8 22.2s19.3 1.7 26.2-5.2l41.6-41.6c87.6 86.5 228.7 86.2 315.8-1c24.4-24.4 42.1-53.1 52.9-83.7c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.2 62.2-162.7 62.5-225.3 1L185 329c6.9-6.9 8.9-17.2 5.2-26.2s-12.5-14.8-22.2-14.8H48.4h-.7H40c-13.3 0-24 10.7-24 24z"></path>
													</svg>
												</button>
												<button
													onClick={() => setModalOpen(true)}
													className="px-4 py-2 text-sm font-light text-gray-900 bg-white border-t border-b border-r border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:text-white dark:hover:bg-gray-700 dark:focus:ring-blue-500 dark:focus:text-white">
													<svg
														className="w-[15px] h-[15px] fill-[#ffffff]"
														viewBox="0 0 448 512"
														xmlns="http://www.w3.org/2000/svg">
														<path d="M246.6 9.4c-12.5-12.5-32.8-12.5-45.3 0l-128 128c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 109.3V320c0 17.7 14.3 32 32 32s32-14.3 32-32V109.3l73.4 73.4c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-128-128zM64 352c0-17.7-14.3-32-32-32s-32 14.3-32 32v64c0 53 43 96 96 96H352c53 0 96-43 96-96V352c0-17.7-14.3-32-32-32s-32 14.3-32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V352z"></path>
													</svg>
												</button>
												<button
													onClick={downloadAudioFile}
													type="button"
													className="px-4 py-2 text-sm font-light text-gray-900 bg-white border-t border-b border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:text-white dark:hover:bg-gray-700 dark:focus:ring-blue-500 dark:focus:text-white">
													<svg
														className="w-[15px] h-[15px] fill-[#ffffff]"
														viewBox="0 0 512 512"
														xmlns="http://www.w3.org/2000/svg">
														<path d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32V274.7l-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7V32zM64 352c-35.3 0-64 28.7-64 64v32c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V416c0-35.3-28.7-64-64-64H346.5l-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352H64zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z"></path>
													</svg>
												</button>
												<button
													onClick={() => handleFileDelete(id, data?.audio_file_name)}
													type="button"
													className="px-4 py-2 text-sm font-light text-gray-900 bg-white border border-gray-200 rounded-e-lg hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:text-white dark:hover:bg-gray-700 dark:focus:ring-blue-500 dark:focus:text-white">
													<svg
														className="w-[15px] h-[15px] fill-[#ffffff]"
														viewBox="0 0 448 512"
														xmlns="http://www.w3.org/2000/svg">
														<path d="M170.5 51.6L151.5 80h145l-19-28.4c-1.5-2.2-4-3.6-6.7-3.6H177.1c-2.7 0-5.2 1.3-6.7 3.6zm147-26.6L354.2 80H368h48 8c13.3 0 24 10.7 24 24s-10.7 24-24 24h-8V432c0 44.2-35.8 80-80 80H112c-44.2 0-80-35.8-80-80V128H24c-13.3 0-24-10.7-24-24S10.7 80 24 80h8H80 93.8l36.7-55.1C140.9 9.4 158.4 0 177.1 0h93.7c18.7 0 36.2 9.4 46.6 24.9zM80 128V432c0 17.7 14.3 32 32 32H336c17.7 0 32-14.3 32-32V128H80zm80 64V400c0 8.8-7.2 16-16 16s-16-7.2-16-16V192c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0V400c0 8.8-7.2 16-16 16s-16-7.2-16-16V192c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0V400c0 8.8-7.2 16-16 16s-16-7.2-16-16V192c0-8.8 7.2-16 16-16s16 7.2 16 16z"></path>
													</svg>
												</button>
											</div>
										</div>
									)}
									{replacingFile && (
										<div>
											<button
												className="border border-slate-600 focus:outline-none font-light rounded-lg text-sm px-4 py-2 me-2 dark:bg-gray-800 dark:text-white"
												onClick={cancelReplaceFile}>
												Cancel
											</button>
										</div>
									)}
								</div>
								<div className="mb-2 items-center justify-between space-y-4 md:space-y-0 pb-2">
									{!showPlayer && !loading && (
										<Dropzone onDrop={onDrop} multiple={false} accept={acceptedTypes}>
											{({ getRootProps, getInputProps }) => (
												<section>
													<div {...getRootProps()}>
														<input {...getInputProps()} />
														<form className="w-full shadow-sm">
															<div
																className="cursor-pointer p-12 flex justify-center bg-gray-800 border border-dashed border-gray-600 rounded-xl"
																data-hs-file-upload-trigger="">
																<div className="text-center">
																	<img
																		src="../../../assets/icons/wave-file-icon.png"
																		className="upload-icon mx-auto"
																		alt="Upload"
																	/>
																	<div className="mt-4 flex flex-wrap justify-center text-sm leading-6 text-gray-600">
																		<span className="pe-1 font-medium text-gray-800 dark:text-neutral-200">
																			Drop your song file here or
																		</span>
																		<span className="bg-white font-semibold text-blue-600 hover:text-blue-700 rounded-lg decoration-2 hover:underline focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 dark:bg-neutral-800 dark:text-blue-500 dark:hover:text-blue-600">
																			browse
																		</span>
																	</div>

																	<p className="mt-1 text-xs text-gray-400 dark:text-neutral-400">
																		WAV or MP3 files accepted
																	</p>
																</div>
															</div>

															<div className="mt-4 space-y-3 empty:mt-0" data-hs-file-upload-previews=""></div>
														</form>
													</div>
												</section>
											)}
										</Dropzone>
									)}
									{loading && !showPlayer && (
										<div className="py-4">
											<div className="flex justify-between mb-1">
												<span className="text-base font-medium text-blue-700 dark:text-white">{fileName}</span>
												<span className="text-sm font-medium text-blue-700 dark:text-white">{progress.toFixed(0)}%</span>
											</div>
											<div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
												<div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress.toFixed(0)}%` }}></div>
											</div>
										</div>
									)}
									{showPlayer && (
										<div className="text-center mt-24px py-4 px-5 border border-2 border-gray-700 w-full rounded-lg shadow-sm bg-transparent">
											<p className="text-lg text-gray-300 mb-2">{data.audio_file_name}</p>
											{isWaveLoading && (
												<div className="text-center audio-loading">
													<div role="status">
														<svg
															aria-hidden="true"
															className="w-10 h-10 absolute inline text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
															viewBox="0 0 100 101"
															fill="none"
															xmlns="http://www.w3.org/2000/svg">
															<path
																d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
																fill="currentColor"
															/>
															<path
																d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
																fill="currentFill"
															/>
														</svg>
														<span className="sr-only">Loading...</span>
													</div>
												</div>
											)}
											<WavesurferPlayer
												height={50}
												barWidth={4}
												barRadius={6}
												barGap={2}
												cursorWidth={2}
												normalize={true}
												mediaControls={false}
												waveColor="black"
												progressColor="rgba(234, 178, 8, 0.87)"
												url={data.audio_url}
												onReady={onReady}
												onPlay={() => setIsPlaying(true)}
												onPause={() => setIsPlaying(false)}
												sampleRate={48000}
											/>
											<div className="mt-3 p-4 ">
												<button
													className="inline-flex h-[45px] w-[45px] items-center justify-center rounded-full bg-gray-800 text-white drop-shadow-sm transition-colors duration-150 hover:bg-gray-200"
													onClick={onPlayPause}>
													{isPlaying ? (
														<svg
															xmlns="http://www.w3.org/2000/svg"
															viewBox="0 0 24 24"
															width="24"
															height="24"
															fill="currentColor">
															<path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
														</svg>
													) : (
														<svg viewBox="0 0 24 24" fill="currentColor" height="24" width="24">
															<path d="M8 5v14l11-7z" />
														</svg>
													)}
												</button>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
					<ShareConfigurationModal
						shareAudioUrl={data.audio_url}
						isOpen={isModalOpen}
						onClose={() => setModalOpen(false)}
						songShareName={data.internal_song_name}
						parentId={id}
					/>
				</div>
				<script src="https://unpkg.com/dropzone@5/dist/min/dropzone.min.js"></script>
				<link rel="stylesheet" href="https://unpkg.com/dropzone@5/dist/min/dropzone.min.css" type="text/css" />
			</div>
		);
	}
};

export default HomeView;
